export interface VideoSlotConstraints {
  videoMaxFileSize?: number;
  videoMinDuration?: number;
  videoMaxDuration?: number;
  videoMinWidth?: number;
  videoMaxWidth?: number;
  videoMinHeight?: number;
  videoMaxHeight?: number;
  videoMinPixels?: number;
  videoMaxPixels?: number;
}

export interface MeasuredVideo {
  width?: number;
  height?: number;
  duration?: number;
}

export interface VideoConstraintMessages {
  durationTooShort?: string;
  durationTooLong?: string;
  videoWidthTooSmall?: string;
  videoWidthTooLarge?: string;
  videoHeightTooSmall?: string;
  videoHeightTooLarge?: string;
  videoPixelsTooSmall?: string;
  videoPixelsTooLarge?: string;
}

// Workflow config_data can also arrive from API/import paths that do not apply the
// admin form's number coercion. Preserve the legacy runtime behavior for positive
// numeric strings and fractional legacy limits while keeping the three new fields
// aligned with their documented positive-integer contract.
const getPositiveFiniteLimit = (limit: unknown): number | undefined => {
  const numericLimit = typeof limit === 'number' || typeof limit === 'string'
    ? Number(limit)
    : Number.NaN;
  return Number.isFinite(numericLimit) && numericLimit > 0 ? numericLimit : undefined;
};

const getPositiveIntegerLimit = (limit: unknown): number | undefined => {
  const numericLimit = getPositiveFiniteLimit(limit);
  return numericLimit !== undefined && Number.isInteger(numericLimit)
    ? numericLimit
    : undefined;
};

export function normalizeVideoSlotConstraints(
  constraints: VideoSlotConstraints,
): VideoSlotConstraints {
  return {
    videoMaxFileSize: getPositiveFiniteLimit(constraints.videoMaxFileSize),
    videoMinDuration: getPositiveIntegerLimit(constraints.videoMinDuration),
    videoMaxDuration: getPositiveFiniteLimit(constraints.videoMaxDuration),
    videoMinWidth: getPositiveFiniteLimit(constraints.videoMinWidth),
    videoMaxWidth: getPositiveFiniteLimit(constraints.videoMaxWidth),
    videoMinHeight: getPositiveFiniteLimit(constraints.videoMinHeight),
    videoMaxHeight: getPositiveFiniteLimit(constraints.videoMaxHeight),
    videoMinPixels: getPositiveIntegerLimit(constraints.videoMinPixels),
    videoMaxPixels: getPositiveIntegerLimit(constraints.videoMaxPixels),
  };
}

export function hasVideoMetadataConstraints(constraints?: VideoSlotConstraints): boolean {
  if (!constraints) return false;
  const normalized = normalizeVideoSlotConstraints(constraints);
  return [
    normalized.videoMinDuration,
    normalized.videoMaxDuration,
    normalized.videoMinWidth,
    normalized.videoMaxWidth,
    normalized.videoMinHeight,
    normalized.videoMaxHeight,
    normalized.videoMinPixels,
    normalized.videoMaxPixels,
  ].some(limit => limit !== undefined);
}

export function validateVideoSlotConstraints(
  measured: MeasuredVideo,
  constraints: VideoSlotConstraints,
  messages?: VideoConstraintMessages,
): string | null {
  const { duration, width, height } = measured;
  const normalized = normalizeVideoSlotConstraints(constraints);
  const minDuration = normalized.videoMinDuration;
  const maxDuration = normalized.videoMaxDuration;

  // Non-finite browser duration values (notably Infinity for media without a
  // settled duration box) are unavailable metadata and retain fail-open behavior.
  if (duration !== undefined && Number.isFinite(duration)) {
    if (minDuration !== undefined && duration < minDuration) {
      return `${messages?.durationTooShort || 'Duration too short'} (${duration}s < min ${minDuration}s)`;
    }
    if (maxDuration !== undefined && duration > maxDuration) {
      return `${messages?.durationTooLong || 'Duration too long'} (${duration}s > max ${maxDuration}s)`;
    }
  }

  // Browser metadata supplies dimensions as a pair. Treat an incomplete pair as
  // unavailable so every resolution-based constraint retains fail-open behavior.
  // A zero/non-finite dimension is also unavailable browser metadata, not a
  // measured zero-pixel video (some codecs report zero before dimensions settle).
  if (
    width === undefined || height === undefined
    || !Number.isFinite(width) || !Number.isFinite(height)
    || width <= 0 || height <= 0
  ) {
    return null;
  }

  const minWidth = normalized.videoMinWidth;
  const maxWidth = normalized.videoMaxWidth;
  const minHeight = normalized.videoMinHeight;
  const maxHeight = normalized.videoMaxHeight;
  const minPixels = normalized.videoMinPixels;
  const maxPixels = normalized.videoMaxPixels;

  if (minWidth !== undefined && width < minWidth) {
    return `${messages?.videoWidthTooSmall || 'Video width too small'} (${width}px < min ${minWidth}px)`;
  }
  if (maxWidth !== undefined && width > maxWidth) {
    return `${messages?.videoWidthTooLarge || 'Video width too large'} (${width}px > max ${maxWidth}px)`;
  }
  if (minHeight !== undefined && height < minHeight) {
    return `${messages?.videoHeightTooSmall || 'Video height too small'} (${height}px < min ${minHeight}px)`;
  }
  if (maxHeight !== undefined && height > maxHeight) {
    return `${messages?.videoHeightTooLarge || 'Video height too large'} (${height}px > max ${maxHeight}px)`;
  }

  const pixels = width * height;
  if (minPixels !== undefined && pixels < minPixels) {
    return `${messages?.videoPixelsTooSmall || 'Video pixel count too small'} (${pixels} < min ${minPixels})`;
  }
  if (maxPixels !== undefined && pixels > maxPixels) {
    return `${messages?.videoPixelsTooLarge || 'Video pixel count too large'} (${pixels} > max ${maxPixels})`;
  }

  return null;
}

export function describeVideoSlotConstraints(constraints: VideoSlotConstraints): string[] {
  const descriptions: string[] = [];
  const normalized = normalizeVideoSlotConstraints(constraints);
  const maxFileSize = normalized.videoMaxFileSize;
  const minDuration = normalized.videoMinDuration;
  const maxDuration = normalized.videoMaxDuration;
  const minWidth = normalized.videoMinWidth;
  const maxWidth = normalized.videoMaxWidth;
  const minHeight = normalized.videoMinHeight;
  const maxHeight = normalized.videoMaxHeight;
  const minPixels = normalized.videoMinPixels;
  const maxPixels = normalized.videoMaxPixels;

  if (maxFileSize !== undefined) {
    descriptions.push(`max ${maxFileSize}MB`);
  }

  const hasMinDuration = minDuration !== undefined;
  const hasMaxDuration = maxDuration !== undefined;
  if (hasMinDuration && hasMaxDuration) {
    descriptions.push(`${minDuration}–${maxDuration}s`);
  } else if (hasMinDuration) {
    descriptions.push(`min ${minDuration}s`);
  } else if (hasMaxDuration) {
    descriptions.push(`max ${maxDuration}s`);
  }

  if (minWidth !== undefined || maxWidth !== undefined) {
    descriptions.push(`W: ${minWidth ?? '?'}-${maxWidth ?? '?'}px`);
  }
  if (minHeight !== undefined || maxHeight !== undefined) {
    descriptions.push(`H: ${minHeight ?? '?'}-${maxHeight ?? '?'}px`);
  }
  if (minPixels !== undefined) {
    descriptions.push(`≥${minPixels} px`);
  }
  if (maxPixels !== undefined) {
    descriptions.push(`≤${maxPixels} px`);
  }

  return descriptions;
}
